import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { ChevronDown, ChevronUp, ChevronsDown, Download, Flag, Plus } from "react-feather";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import {
  createTeacherEscalation,
  getActiveTeachers,
  getTeacherEscalationById,
  getTeacherEscalationByTeacher,
  getTeacherEscalationList,
  getTeachers,
  teacherProfile,
  validateTeacherEscalation,
  recordTeacherEscalationAction,
  type TeacherEscalation,
  type TeacherEscalationByTeacherRow,
  type TeacherEscalationStats,
  type TeacherEscalationValidationStatus,
} from "../../../../Api/apiUtils";
import { ConfigContext } from "../../../../Api/global/ApiContext";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  TextInput,
  Select,
  Toolbar,
  Badge,
  Table,
  DescriptionList,
  Loader,
  EmptyState,
} from "../../../../ui";
import type { BadgeTone } from "../../../../ui";
import Tabs from "./ui-pending/Tabs";
import Pager from "./ui-pending/Pager";
import TextArea from "./ui-pending/TextArea";
import StatTile from "./ui-pending/StatTile";
import "./teacherEscalation.scss";

type Priority = "Low" | "Medium" | "High" | "Urgent";

type EscalationCategory = "CE" | "Academics" | "L&D" | "Teacher OPs" | "Sales and Renewals";

type ActionTaken =
  | "Verbal Warning 1"
  | "Verbal Warning 2"
  | "Warning 1 sent"
  | "Warning 2 sent"
  | "Warning 3 sent"
  | "PIP"
  | "Termination"
  | "Other";

type EscalationTab = "all" | "validated" | "byTeacher";

interface RaiseFormState {
  teacherUserId: number | null;
  teacherName: string;
  teacherPhone: string;
  teacherQuery: string;
  teacherGenre: string;
  studentUserId: string;
  category: EscalationCategory | "";
  subcategory: string;
  priority: Priority | "";
  actionTaken: ActionTaken | "";
  description: string;
  link: string;
}

interface TeacherSuggestion {
  teacherId: string;
  teacherUserId?: number | null;
  name: string;
  phone: string;
  courseCategory?: string;
}

interface TeacherFilterOption {
  userId: number;
  label: string;
}

const EMPTY_RAISE_FORM: RaiseFormState = {
  teacherUserId: null,
  teacherName: "",
  teacherPhone: "",
  teacherQuery: "",
  teacherGenre: "",
  studentUserId: "",
  category: "",
  subcategory: "",
  priority: "",
  actionTaken: "",
  description: "",
  link: "",
};

const REASON_MAX = 500;
const REASON_TABLE_PREVIEW = 120;
const PRIORITY_OPTIONS: Priority[] = ["Low", "Medium", "High", "Urgent"];
const PAGE_SIZE = 20;
const BY_TEACHER_PAGE_SIZE = 50;

const BY_TEACHER_SORT_OPTIONS = [
  { value: "mostValid", label: "Most valid" },
  { value: "mostAll", label: "Most escalations" },
  { value: "recent", label: "Recent escalations" },
];

/** PRD: CE must be first */
const ESCALATION_CATEGORIES: EscalationCategory[] = [
  "CE",
  "Academics",
  "L&D",
  "Teacher OPs",
  "Sales and Renewals",
];

const SUBCATEGORY_BY_CATEGORY: Record<EscalationCategory, string[]> = {
  CE: [
    "Class Experience",
    "Teacher Behaviour",
    "Communication",
    "Support Handling",
    "Other",
  ],
  Academics: [
    "Curriculum",
    "Pace",
    "Lesson Marking",
    "Content Quality",
    "Assessment",
    "Other",
  ],
  "L&D": [
    "Training Gap",
    "Soft Skills",
    "Process Adherence",
    "Coaching Need",
    "Other",
  ],
  "Teacher OPs": [
    "Attendance / Punctuality",
    "Slot / Availability",
    "Leave / Cancellation",
    "Conduct",
    "Other",
  ],
  "Sales and Renewals": [
    "Renewal Concern",
    "Cross-sell / Upsell Issue",
    "Payment Related",
    "Package Confusion",
    "Other",
  ],
};

const ACTION_TAKEN_OPTIONS: ActionTaken[] = [
  "Verbal Warning 1",
  "Verbal Warning 2",
  "Warning 1 sent",
  "Warning 2 sent",
  "Warning 3 sent",
  "PIP",
  "Termination",
  "Other",
];

const VALIDATION_FILTERS: Array<{ value: ""; label: string } | { value: TeacherEscalationValidationStatus; label: string }> = [
  { value: "", label: "All statuses" },
  { value: "PENDING", label: "Open" },
  { value: "VALID", label: "Valid" },
  { value: "INVALID", label: "Invalid" },
];

const FRESHDESK_STATUS_FILTERS: Array<{ value: ""; label: string } | { value: string; label: string }> = [
  { value: "", label: "All FD statuses" },
  { value: "Open", label: "FD Open" },
  { value: "Resolved", label: "FD Resolved" },
  { value: "Closed", label: "FD Closed" },
];

const toLocalYmd = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultFromDate = () => {
  const now = new Date();
  return toLocalYmd(new Date(now.getFullYear(), now.getMonth(), 1));
};

// Category chip tone; same category matching as before, mapped to Badge tones.
const categoryTone = (category?: string | null): BadgeTone => {
  const value = (category || "").toLowerCase();
  if (value === "ce") return "warning";
  if (value.includes("academic")) return "info";
  if (value.includes("l&d") || value.includes("l and d")) return "success";
  if (value.includes("ops") || value.includes("teacher op")) return "danger";
  if (value.includes("sales") || value.includes("renewal")) return "accent";
  return "primary";
};

const formatDateOnly = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDisplayDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getEscalationGenreName = (row: TeacherEscalation): string =>
  row.genre?.name || row.courseCategory || row.teacherGenre || "";

const getCurrentAdminIdentity = () => {
  try {
    const profile = JSON.parse(localStorage.getItem("profileData") || "{}");
    return profile.emailId || profile.userId || localStorage.getItem("loginUser") || "UI";
  } catch {
    return "UI";
  }
};

// VALID / INVALID / PENDING are not in StatusBadge's map yet, so map them here.
const validationTone = (status?: string | null): BadgeTone => {
  const s = String(status || "").toUpperCase();
  if (s === "VALID") return "success";
  if (s === "INVALID") return "danger";
  return "warning";
};

const validationLabel = (status?: string | null) => {
  const s = String(status || "").toUpperCase();
  if (s === "VALID") return "✓ Valid";
  if (s === "INVALID") return "✗ Invalid";
  return "Pending";
};

const truncateDescriptionPreview = (text: string, maxLen: number) => {
  if (text.length <= maxLen) return text;
  const slice = text.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > maxLen * 0.55 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trim()}…`;
};

const EscalationDescriptionCell: React.FC<{ description?: string | null; link?: string | null }> = ({
  description,
  link,
}) => {
  const [expanded, setExpanded] = useState(false);
  const text = (description || "").trim();
  if (!text) return <>—</>;

  const needsTruncate = text.length > REASON_TABLE_PREVIEW;
  const preview = truncateDescriptionPreview(text, REASON_TABLE_PREVIEW);

  return (
    <>
      <div className="esc-reason-text">{expanded || !needsTruncate ? text : preview}</div>
      {needsTruncate && (
        <Button
          variant="ghost"
          size="sm"
          className="esc-read-more-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Read less" : "Read more"}
        </Button>
      )}
      {link && (
        <div className="esc-meta">
          <a href={link} target="_blank" rel="noreferrer">
            Evidence / Ticket
          </a>
        </div>
      )}
    </>
  );
};

const getEscalationActionNotes = (row: TeacherEscalation): string =>
  String(row.actionNotes ?? row.actionTaken ?? "").trim();

const applyClientRowFilters = (
  sourceRows: TeacherEscalation[],
  filters: {
    activeTab: EscalationTab;
    subcategoryFilter: string;
  }
) => {
  return sourceRows.filter((row) => {
    // Validated Log rows come from the edited-on API; don't drop PENDING here or the
    // table length drifts from that API's totalElements badge.
    if (filters.subcategoryFilter && String(row.subcategory || "") !== filters.subcategoryFilter) return false;
    return true;
  });
};

const buildEscalationCsv = (exportRows: TeacherEscalation[]) => {
  const escapeCsvCell = (value: unknown) => {
    const s = value === null || value === undefined ? "" : String(value);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const headers = [
    "ID",
    "Teacher User Id",
    "Teacher Name",
    "Teacher Mobile",
    "Student User Id",
    "Teacher Genre",
    "Category",
    "Subcategory",
    "Description",
    "Priority",
    "Action Taken",
    "Validation Status",
    "Validation Notes",
    "Freshdesk Ticket Id",
    "Freshdesk Status",
    "Created By",
    "Created On",
    "Edited By",
    "Edited On",
    "Link",
  ];
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...exportRows.map((row) =>
      [
        row.id,
        row.teacherUserId,
        row.teacherName,
        row.teacherMobileNumber,
        row.studentUserId,
        getEscalationGenreName(row),
        row.category,
        row.subcategory,
        row.description,
        row.priority,
        row.actionNotes ?? row.actionTaken,
        row.validationStatus,
        row.validationNotes,
        row.freshdeskTicketId,
        row.freshdeskStatus,
        row.createdBy,
        row.createdOn,
        row.editedBy,
        row.editedOn,
        row.link,
      ]
        .map(escapeCsvCell)
        .join(",")
    ),
  ];
  return `\uFEFF${lines.join("\n")}`;
};

const buildByTeacherCsv = (teacherRows: TeacherEscalationByTeacherRow[]): string => {
  const escapeCsvCell = (value: unknown) => {
    const s = value === null || value === undefined ? "" : String(value);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = [
    ["Teacher", "Teacher User Id", "Genre", "All", "Valid", "Invalid", "Last Escalation", "Flagged"]
      .map(escapeCsvCell)
      .join(","),
    ...teacherRows.map((row) =>
      [
        row.teacherName || `User #${row.teacherUserId}`,
        row.teacherUserId,
        row.genre?.name || "",
        row.all,
        row.valid,
        row.invalid,
        row.lastEscalationOn || "",
        row.flagged ? "Yes" : "No",
      ]
        .map(escapeCsvCell)
        .join(",")
    ),
  ];
  return `\uFEFF${lines.join("\n")}`;
};

const TeacherEscalationPage: React.FC = () => {
  const [rows, setRows] = useState<TeacherEscalation[]>([]);
  const [natureRows, setNatureRows] = useState<TeacherEscalation[]>([]);
  const [stats, setStats] = useState<TeacherEscalationStats>({
    open: 0,
    validatedValid: 0,
    validatedInvalid: 0,
    resolvedThisMonth: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [validationStatus, setValidationStatus] = useState<"" | TeacherEscalationValidationStatus>("");
  const [freshdeskStatusFilter, setFreshdeskStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [genreIdFilter, setGenreIdFilter] = useState("");
  const [actionTakenFilter, setActionTakenFilter] = useState("");
  const [teacherUserIdFilter, setTeacherUserIdFilter] = useState("");
  const [ticketIdFilter, setTicketIdFilter] = useState("");
  const [createdFrom, setCreatedFrom] = useState(getDefaultFromDate);
  const [createdTo, setCreatedTo] = useState(() => toLocalYmd(new Date()));
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [sortBy, setSortBy] = useState("createdOn");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [teacherFilterOptions, setTeacherFilterOptions] = useState<TeacherFilterOption[]>([]);
  const [teachersFilterLoading, setTeachersFilterLoading] = useState(false);
  const [teacherFilterQuery, setTeacherFilterQuery] = useState("");
  const [showTeacherFilterMenu, setShowTeacherFilterMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<EscalationTab>("all");
  const [teacherRows, setTeacherRows] = useState<TeacherEscalationByTeacherRow[]>([]);
  const [flagThreshold, setFlagThreshold] = useState(2);
  const [byTeacherSort, setByTeacherSort] = useState("mostValid");
  const [flaggedTeachers, setFlaggedTeachers] = useState(0);
  const [teacherTotalElements, setTeacherTotalElements] = useState(0);
  /** Badge = created-axis API totalElements */
  const [allTabTotal, setAllTabTotal] = useState(0);
  /** Badge = edited-axis API totalElements */
  const [validatedTabTotal, setValidatedTabTotal] = useState(0);

  const handleCreatedRangeChange = (dates: DateObject | DateObject[] | null) => {
    if (!Array.isArray(dates)) return;

    setCreatedFrom(dates[0]?.format("YYYY-MM-DD") || "");
    setCreatedTo(dates[1]?.format("YYYY-MM-DD") || "");
    setPage(0);
  };

  const createdRangeValue = useMemo(
    () =>
      [createdFrom, createdTo]
        .filter(Boolean)
        .map((date) => new DateObject({ date, format: "YYYY-MM-DD" })),
    [createdFrom, createdTo],
  );

  const toggleRaisedBySort = () => {
    setSortOrder((currentOrder) => (sortBy === "createdBy" && currentOrder === "asc" ? "desc" : "asc"));
    setSortBy("createdBy");
    setPage(0);
  };

  const selectTab = (tab: EscalationTab) => {
    setActiveTab(tab);
    setValidationStatus("");
    setPage(0);
    trackEvent(MixpanelEvents.TEACHER_ESCALATION_TAB_CHANGED, { tab });
  };

  const config = useContext(ConfigContext);
  const genreOptions: Array<{ id: number | string; name: string }> = useMemo(() => {
    const fromConfig = (config?.AirlineCourseCategory || []).map((item: any) => ({
      id: item.id,
      name: item.name,
    }));
    if (fromConfig.length) return fromConfig;
    // fallback from currently loaded rows
    const map = new Map<string, { id: number | string; name: string }>();
    rows.forEach((row) => {
      if (row.genre?.id != null && row.genre?.name) {
        map.set(String(row.genre.id), { id: row.genre.id, name: row.genre.name });
      }
    });
    return Array.from(map.values());
  }, [config, rows]);

  // Raise form genres come from AirlineCourseCategory in config.json; the auto-filled
  // genre of the picked teacher is kept as an option even when config has no such name
  const raiseGenreOptions: string[] = useMemo(() => {
    const names = (config?.AirlineCourseCategory || [])
      .map((item: any) => String(item?.name || "").trim())
      .filter(Boolean);
    return Array.from(new Set<string>(names));
  }, [config]);

  const [raiseModalOpen, setRaiseModalOpen] = useState(false);
  const [raiseForm, setRaiseForm] = useState<RaiseFormState>(EMPTY_RAISE_FORM);
  const [teacherSuggestions, setTeacherSuggestions] = useState<TeacherSuggestion[]>([]);
  const [showTeacherSuggestions, setShowTeacherSuggestions] = useState(false);
  const [teacherSearchLoading, setTeacherSearchLoading] = useState(false);
  const [submittingRaise, setSubmittingRaise] = useState(false);
  const teacherSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRow, setDetailRow] = useState<TeacherEscalation | null>(null);

  const [validateModalOpen, setValidateModalOpen] = useState(false);
  const [validateTarget, setValidateTarget] = useState<{
    id: number;
    teacherName?: string | null;
    category?: string | null;
    validationStatus: "VALID" | "INVALID";
  } | null>(null);
  const [validateNotes, setValidateNotes] = useState("");
  const [submittingValidate, setSubmittingValidate] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [actionSavingId, setActionSavingId] = useState<number | null>(null);

  const currentUserEmail = useMemo(() => {
    try {
      const profileData = JSON.parse(localStorage.getItem("profileData") || "{}");
      return String(profileData.emailId || "").trim();
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTeacherFilterOptions = async () => {
      setTeachersFilterLoading(true);
      try {
        const teachers = await getActiveTeachers();
        if (cancelled) return;
        const options = (Array.isArray(teachers) ? teachers : [])
          .map((t: {
            userId?: number | string | null;
            firstName?: string | null;
            lastName?: string | null;
            teacherName?: string | null;
            name?: string | null;
            mobileNumber?: string | null;
          }) => {
            const userId = t.userId != null ? Number(t.userId) : NaN;
            if (!Number.isFinite(userId)) return null;
            const name =
              [t.firstName, t.lastName].filter(Boolean).join(" ").trim() ||
              String(t.teacherName || t.name || "").trim();
            if (!name) return null;
            const phone = t.mobileNumber ? ` · ${t.mobileNumber}` : "";
            return { userId, label: `${name}${phone}` };
          })
          .filter((opt): opt is TeacherFilterOption => opt != null)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
        setTeacherFilterOptions(options);
      } catch (error) {
        console.error("Failed to load teachers for filter", error);
        if (!cancelled) setTeacherFilterOptions([]);
      } finally {
        if (!cancelled) setTeachersFilterLoading(false);
      }
    };
    void loadTeacherFilterOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Filters shared by both list axes — dates are applied per-call (created vs edited). */
  const listQueryBase = useMemo(
    () => ({
      sortOrder,
      includeDeleted,
      ...(validationStatus ? { validationStatus } : {}),
      ...(freshdeskStatusFilter.trim() ? { freshdeskStatus: freshdeskStatusFilter.trim() } : {}),
      ...(teacherUserIdFilter.trim() ? { teacherUserId: teacherUserIdFilter.trim() } : {}),
      ...(ticketIdFilter.trim() ? { freshdeskTicketId: ticketIdFilter.trim() } : {}),
      ...(genreIdFilter ? { genreId: genreIdFilter } : {}),
      ...(actionTakenFilter.trim() ? { actionNotes: actionTakenFilter.trim() } : {}),
    }),
    [
      sortOrder,
      includeDeleted,
      validationStatus,
      freshdeskStatusFilter,
      teacherUserIdFilter,
      ticketIdFilter,
      genreIdFilter,
      actionTakenFilter,
    ],
  );

  /**
   * Two separate list calls (never both date axes on one URL):
   * - createdFrom/To → All Escalations data + badge (totalElements)
   * - editedFrom/To → Validated Log data + badge (totalElements)
   */
  const fetchList = useCallback(async () => {
    if (activeTab === "byTeacher") return;
    setLoading(true);
    try {
      const categoryParam = categoryFilter.trim() ? { category: categoryFilter.trim() } : {};
      const shared = { ...listQueryBase, ...categoryParam, size: PAGE_SIZE };

      const [createdRes, editedRes] = await Promise.all([
        getTeacherEscalationList({
          ...shared,
          sortBy: sortBy === "editedOn" ? "createdOn" : sortBy,
          page: activeTab === "all" ? page : 0,
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTo ? { createdTo } : {}),
        }),
        getTeacherEscalationList({
          ...shared,
          sortBy: "editedOn",
          page: activeTab === "validated" ? page : 0,
          ...(createdFrom ? { editedFrom: createdFrom } : {}),
          ...(createdTo ? { editedTo: createdTo } : {}),
        }),
      ]);

      setAllTabTotal(createdRes.totalElements);
      setValidatedTabTotal(editedRes.totalElements);
      setNatureRows(createdRes.content);
      if (createdRes.stats) setStats(createdRes.stats);
      else if (editedRes.stats) setStats(editedRes.stats);

      if (activeTab === "validated") {
        setRows(editedRes.content);
        setTotalElements(editedRes.totalElements);
        setTotalPages(editedRes.totalPages);
      } else {
        setRows(createdRes.content);
        setTotalElements(createdRes.totalElements);
        setTotalPages(createdRes.totalPages);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to load escalations.");
      setRows([]);
      setNatureRows([]);
      setAllTabTotal(0);
      setValidatedTabTotal(0);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    listQueryBase,
    page,
    categoryFilter,
    createdFrom,
    createdTo,
    sortBy,
  ]);

  const fetchByTeacher = useCallback(async () => {
    const showTable = activeTab === "byTeacher";
    if (showTable) setLoading(true);
    try {
      const data = await getTeacherEscalationByTeacher({
        flagThreshold,
        flaggedOnly: true,
        sortBy: byTeacherSort,
        page: showTable ? page : 0,
        size: showTable ? BY_TEACHER_PAGE_SIZE : 1,
        ...(teacherUserIdFilter.trim() ? { teacherUserId: teacherUserIdFilter.trim() } : {}),
        ...(genreIdFilter ? { genreId: genreIdFilter } : {}),
        ...(categoryFilter.trim() ? { category: categoryFilter.trim() } : {}),
        ...(validationStatus ? { validationStatus } : {}),
        ...(createdFrom ? { createdFrom } : {}),
        ...(createdTo ? { createdTo } : {}),
      });
      setTeacherTotalElements(data.totalElements);
      setFlaggedTeachers(data.flaggedTeachers);
      if (showTable) {
        setTeacherRows(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
      }
    } catch (error: any) {
      console.error(error);
      if (showTable) {
        toast.error(error?.response?.data?.message || "Failed to load teachers.");
        setTeacherRows([]);
        setTotalPages(0);
        setTotalElements(0);
        setFlaggedTeachers(0);
      }
    } finally {
      if (showTable) setLoading(false);
    }
  }, [
    activeTab,
    flagThreshold,
    byTeacherSort,
    page,
    teacherUserIdFilter,
    genreIdFilter,
    categoryFilter,
    validationStatus,
    createdFrom,
    createdTo,
  ]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    void fetchByTeacher();
  }, [fetchByTeacher]);

  useEffect(() => {
    return () => {
      if (teacherSearchTimer.current) clearTimeout(teacherSearchTimer.current);
    };
  }, []);

  useEffect(() => {
    trackEvent(MixpanelEvents.TEACHER_ESCALATION_PAGE_VIEW);
  }, []);

  const filteredRows = useMemo(
    () =>
      applyClientRowFilters(rows, {
        activeTab,
        subcategoryFilter,
      }),
    [rows, subcategoryFilter, activeTab]
  );

  const complaintNatureCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ESCALATION_CATEGORIES.forEach((cat) => {
      counts[cat] = 0;
    });
    natureRows.forEach((row) => {
      const key = String(row.category || "Other").trim() || "Other";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => {
      const ai = ESCALATION_CATEGORIES.indexOf(a[0] as EscalationCategory);
      const bi = ESCALATION_CATEGORIES.indexOf(b[0] as EscalationCategory);
      if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      return b[1] - a[1];
    });
  }, [natureRows]);

  const raiseSubcategoryOptions = raiseForm.category
    ? SUBCATEGORY_BY_CATEGORY[raiseForm.category]
    : [];

  const filterSubcategoryOptions = categoryFilter && ESCALATION_CATEGORIES.includes(categoryFilter as EscalationCategory)
    ? SUBCATEGORY_BY_CATEGORY[categoryFilter as EscalationCategory]
    : [];

  const openRaiseModal = () => {
    setRaiseForm(EMPTY_RAISE_FORM);
    setTeacherSuggestions([]);
    setShowTeacherSuggestions(false);
    setRaiseModalOpen(true);
    trackEvent(MixpanelEvents.TEACHER_ESCALATION_RAISE_MODAL_OPENED);
  };

  const closeRaiseModal = () => {
    if (submittingRaise) return;
    setRaiseModalOpen(false);
    setRaiseForm(EMPTY_RAISE_FORM);
    setTeacherSuggestions([]);
    setShowTeacherSuggestions(false);
  };

  const searchTeachers = async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setTeacherSuggestions([]);
      return;
    }
    setTeacherSearchLoading(true);
    try {
      const digitsOnly = q.replace(/\D/g, "");
      const payload = digitsOnly.length >= 4 ? { mobileNumber: digitsOnly } : { name: q };
      const list = await getTeachers(0, 20, payload);
      const mapped: TeacherSuggestion[] = (list || []).map((t: any) => ({
        teacherId: String(t.teacherId || t.id || ""),
        teacherUserId: t.userId ? Number(t.userId) : t.teacherUserId ? Number(t.teacherUserId) : null,
        name:
          [t.firstName, t.lastName].filter(Boolean).join(" ").trim() ||
          t.teacherName ||
          t.name ||
          "",
        phone: String(t.mobileNumber || t.phoneNumber || t.phone || ""),
        courseCategory: t.courseCategory || t.courseName || t.categoryName || "",
      })).filter((t: TeacherSuggestion) => t.name && t.teacherId);
      setTeacherSuggestions(mapped);
      setShowTeacherSuggestions(mapped.length > 0);
    } catch (error) {
      console.error("Teacher search failed", error);
      setTeacherSuggestions([]);
    } finally {
      setTeacherSearchLoading(false);
    }
  };

  const handleTeacherQueryChange = (value: string) => {
    setRaiseForm((prev) => ({
      ...prev,
      teacherQuery: value,
      teacherName: value,
      teacherUserId: null,
      teacherPhone: "",
    }));
    if (teacherSearchTimer.current) clearTimeout(teacherSearchTimer.current);
    teacherSearchTimer.current = setTimeout(() => {
      void searchTeachers(value);
    }, 350);
  };

  const selectTeacherSuggestion = async (teacher: TeacherSuggestion) => {
    let teacherUserId = teacher.teacherUserId || null;
    let teacherGenre = teacher.courseCategory || "";
    if (!teacherUserId && teacher.teacherId) {
      try {
        const profile = await teacherProfile(teacher.teacherId);
        teacherUserId = profile?.teacherProfileDetails?.userId
          ? Number(profile.teacherProfileDetails.userId)
          : null;
        if (!teacherGenre) {
          teacherGenre =
            profile?.teacherProfileDetails?.courseName ||
            profile?.teacherProfileDetails?.courseCategory ||
            "";
        }
      } catch (error) {
        console.error("Failed to resolve teacherUserId", error);
      }
    }
    if (!teacherUserId) {
      toast.error("Could not resolve teacher user id. Please try another teacher.");
      return;
    }
    setRaiseForm((prev) => ({
      ...prev,
      teacherUserId,
      teacherName: teacher.name,
      teacherPhone: teacher.phone,
      teacherGenre: teacherGenre || prev.teacherGenre,
      teacherQuery: teacher.phone ? `${teacher.name} (${teacher.phone})` : teacher.name,
    }));
    setShowTeacherSuggestions(false);
  };

  const handleSubmitEscalation = async () => {
    if (!raiseForm.teacherUserId) {
      toast.error("Please select a teacher from search results.");
      return;
    }
    if (!raiseForm.teacherGenre) {
      toast.error("Please select teacher genre / course category.");
      return;
    }
    if (!raiseForm.category) {
      toast.error("Please select a category.");
      return;
    }
    if (!raiseForm.subcategory) {
      toast.error("Please select a subcategory.");
      return;
    }
    if (!raiseForm.priority) {
      toast.error("Please select priority.");
      return;
    }
    if (!raiseForm.description.trim()) {
      toast.error("Please enter a description.");
      return;
    }

    setSubmittingRaise(true);
    try {
      await createTeacherEscalation({
        teacherUserId: raiseForm.teacherUserId,
        ...(raiseForm.studentUserId.trim()
          ? { studentUserId: Number(raiseForm.studentUserId.trim()) }
          : {}),
        category: raiseForm.category,
        subcategory: raiseForm.subcategory,
        description: raiseForm.description.trim(),
        ...(raiseForm.link.trim() ? { link: raiseForm.link.trim() } : {}),
        priority: raiseForm.priority,
        ticketType: "Teacher Escalation",
        createdBy: String(getCurrentAdminIdentity()),
        courseCategory: raiseForm.teacherGenre,
        teacherGenre: raiseForm.teacherGenre,
        ...(raiseForm.actionTaken ? { actionTaken: raiseForm.actionTaken } : {}),
      });
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_RAISED, {
        teacherUserId: raiseForm.teacherUserId,
        category: raiseForm.category,
        subcategory: raiseForm.subcategory,
        priority: raiseForm.priority,
      });
      toast.success("Escalation created.");
      setRaiseModalOpen(false);
      setRaiseForm(EMPTY_RAISE_FORM);
      setPage(0);
      await Promise.all([fetchList(), fetchByTeacher()]);
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (status === 409 ? "Freshdesk ticket id already exists." : "Failed to create escalation.");
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_RAISE_FAILED, {
        teacherUserId: raiseForm.teacherUserId,
        category: raiseForm.category,
        status,
        message,
      });
      toast.error(message);
    } finally {
      setSubmittingRaise(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRow(null);
    try {
      const data = await getTeacherEscalationById(id);
      setDetailRow(data);
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_DETAIL_VIEWED, {
        escalationId: id,
        teacherUserId: data.teacherUserId,
        validationStatus: data.validationStatus,
        category: data.category,
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to load escalation details.");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openValidateModal = (row: TeacherEscalation, validationStatus: "VALID" | "INVALID") => {
    if (row.id == null) return;
    setValidateTarget({
      id: row.id,
      teacherName: row.teacherName,
      category: row.category,
      validationStatus,
    });
    setValidateNotes("");
    setValidateModalOpen(true);
  };

  const closeValidateModal = () => {
    if (submittingValidate) return;
    setValidateModalOpen(false);
    setValidateTarget(null);
    setValidateNotes("");
  };

  const handleSubmitValidation = async () => {
    if (!validateTarget) return;
    const notes = validateNotes.trim();
    if (!notes) {
      toast.warning("Please enter a reason for this validation.");
      return;
    }

    setSubmittingValidate(true);
    try {
      await validateTeacherEscalation(validateTarget.id, {
        validationStatus: validateTarget.validationStatus,
        validationNotes: notes,
        ...(currentUserEmail ? { validatedBy: currentUserEmail } : {}),
      });
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_VALIDATED, {
        escalationId: validateTarget.id,
        validationStatus: validateTarget.validationStatus,
        category: validateTarget.category,
        teacherName: validateTarget.teacherName,
      });
      toast.success(
        validateTarget.validationStatus === "VALID"
          ? "Escalation marked as valid."
          : "Escalation marked as invalid.",
      );
      setValidateModalOpen(false);
      setValidateTarget(null);
      setValidateNotes("");
      void fetchList();
      if (detailOpen && detailRow?.id === validateTarget.id) {
        const updated = await getTeacherEscalationById(validateTarget.id);
        setDetailRow(updated);
      }
    } catch (error: any) {
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_VALIDATION_FAILED, {
        escalationId: validateTarget?.id,
        validationStatus: validateTarget?.validationStatus,
        status: error?.response?.status,
        message: error?.response?.data?.message,
      });
      toast.error(error?.response?.data?.message || "Failed to update validation status.");
    } finally {
      setSubmittingValidate(false);
    }
  };

  const handleRecordAction = async (row: TeacherEscalation, actionNotes: string) => {
    if (row.id == null) return;
    const current = getEscalationActionNotes(row);
    if (actionNotes === current) return;

    setActionSavingId(row.id);
    try {
      const updated = await recordTeacherEscalationAction(row.id, { actionNotes });
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      if (detailRow?.id === updated.id) setDetailRow(updated);
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_ACTION_RECORDED, {
        escalationId: row.id,
        teacherUserId: row.teacherUserId,
        actionNotes: actionNotes || null,
        cleared: !actionNotes,
      });
      toast.success(actionNotes ? "Action recorded." : "Action cleared.");
    } catch (error: any) {
      const status = error?.response?.status;
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_ACTION_FAILED, {
        escalationId: row.id,
        teacherUserId: row.teacherUserId,
        actionNotes: actionNotes || null,
        status,
        message: error?.response?.data?.message,
      });
      if (status === 409) {
        toast.error("Mark the escalation as Valid before recording an action.");
      } else {
        toast.error(error?.response?.data?.message || "Failed to record action.");
      }
    } finally {
      setActionSavingId(null);
    }
  };

  const fetchAllEscalationRowsForExport = useCallback(async () => {
    const pageSize = 200;
    let pageNum = 0;
    let collected: TeacherEscalation[] = [];
    let totalPagesCount = 1;
    const categoryParam = categoryFilter.trim() ? { category: categoryFilter.trim() } : {};
    const useEditedAxis = activeTab === "validated";

    do {
      const data = await getTeacherEscalationList({
        ...listQueryBase,
        ...categoryParam,
        sortBy: useEditedAxis ? "editedOn" : sortBy === "editedOn" ? "createdOn" : sortBy,
        page: pageNum,
        size: pageSize,
        ...(useEditedAxis
          ? {
              ...(createdFrom ? { editedFrom: createdFrom } : {}),
              ...(createdTo ? { editedTo: createdTo } : {}),
            }
          : {
              ...(createdFrom ? { createdFrom } : {}),
              ...(createdTo ? { createdTo } : {}),
            }),
      });
      collected = collected.concat(data.content);
      totalPagesCount = Math.max(data.totalPages, 1);
      pageNum += 1;
    } while (pageNum < totalPagesCount);

    return collected;
  }, [listQueryBase, categoryFilter, activeTab, createdFrom, createdTo, sortBy]);

  const downloadCsv = async () => {
    setCsvDownloading(true);
    try {
      if (activeTab === "byTeacher") {
        const data = await getTeacherEscalationByTeacher({
          flagThreshold,
          flaggedOnly: true,
          sortBy: byTeacherSort,
          page: 0,
          size: Math.max(totalElements || BY_TEACHER_PAGE_SIZE, BY_TEACHER_PAGE_SIZE),
          ...(teacherUserIdFilter.trim() ? { teacherUserId: teacherUserIdFilter.trim() } : {}),
          ...(genreIdFilter ? { genreId: genreIdFilter } : {}),
          ...(categoryFilter.trim() ? { category: categoryFilter.trim() } : {}),
          ...(validationStatus ? { validationStatus } : {}),
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTo ? { createdTo } : {}),
        });
        if (!data.content.length) {
          toast.warning("No teachers to download.");
          return;
        }
        const blob = new Blob([buildByTeacherCsv(data.content)], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `teacher-escalations-by-teacher-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        trackEvent(MixpanelEvents.TEACHER_ESCALATION_CSV_EXPORT, {
          rowCount: data.content.length,
          activeTab,
        });
        toast.success(`CSV downloaded (${data.content.length} row${data.content.length === 1 ? "" : "s"}).`);
        return;
      }

      const allRows = await fetchAllEscalationRowsForExport();
      const exportRows = applyClientRowFilters(allRows, {
        activeTab,
        subcategoryFilter,
      });

      if (!exportRows.length) {
        toast.warning("No escalations to download.");
        return;
      }

      const blob = new Blob([buildEscalationCsv(exportRows)], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `teacher-escalations-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_CSV_EXPORT, {
        rowCount: exportRows.length,
        activeTab,
        categoryFilter: categoryFilter || null,
        teacherUserIdFilter: teacherUserIdFilter || null,
        validationStatus: validationStatus || null,
        actionTakenFilter: actionTakenFilter || null,
        createdFrom,
        createdTo,
      });
      toast.success(`CSV downloaded (${exportRows.length} row${exportRows.length === 1 ? "" : "s"}).`);
    } catch (error: any) {
      trackEvent(MixpanelEvents.TEACHER_ESCALATION_CSV_EXPORT_FAILED, {
        message: error?.response?.data?.message,
      });
      toast.error(error?.response?.data?.message || "Failed to download CSV.");
    } finally {
      setCsvDownloading(false);
    }
  };

  const applyFilters = () => {
    setPage(0);
    void fetchList();
    void fetchByTeacher();
  };

  const tabItems: { key: EscalationTab; label: React.ReactNode }[] = [
    {
      key: "all",
      label: (
        <>
          All Escalations
          <Badge tone="primary">{loading && activeTab === "all" ? "…" : allTabTotal}</Badge>
        </>
      ),
    },
    {
      key: "validated",
      label: (
        <>
          Validated Log
          <Badge tone="success">
            {loading && activeTab === "validated" ? "…" : validatedTabTotal}
          </Badge>
        </>
      ),
    },
    {
      key: "byTeacher",
      label: (
        <>
          Repeat Escalations
          <Badge tone="primary">{loading && activeTab === "byTeacher" ? "…" : teacherTotalElements}</Badge>
        </>
      ),
    },
  ];

  const raiseGenreSelectOptions = [
    { value: "", label: "Select genre" },
    ...(raiseForm.teacherGenre && !raiseGenreOptions.includes(raiseForm.teacherGenre)
      ? [{ value: raiseForm.teacherGenre, label: raiseForm.teacherGenre }]
      : []),
    ...raiseGenreOptions.map((g) => ({ value: g, label: g })),
  ];

  const selectedTeacherFilterLabel = useMemo(() => {
    if (!teacherUserIdFilter) return "All teachers";
    return (
      teacherFilterOptions.find((t) => String(t.userId) === teacherUserIdFilter)?.label ||
      `User #${teacherUserIdFilter}`
    );
  }, [teacherUserIdFilter, teacherFilterOptions]);

  const filteredTeacherFilterOptions = useMemo(() => {
    const q = teacherFilterQuery.trim().toLowerCase();
    if (!q) return teacherFilterOptions;
    return teacherFilterOptions.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        String(t.userId).includes(q),
    );
  }, [teacherFilterOptions, teacherFilterQuery]);

  const pickTeacherFilter = (userId: string) => {
    setTeacherUserIdFilter(userId);
    setPage(0);
    setTeacherFilterQuery("");
    setShowTeacherFilterMenu(false);
  };

  return (
    <Page className="teacher-escalation">
      <PageHeader
        eyebrow="Teacher Operations"
        title="Teacher Escalation"
        subtitle="Review, validate and record actions on teacher escalations (Freshdesk and manual)."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Download size={16} aria-hidden />}
              onClick={() => void downloadCsv()}
              disabled={loading}
              loading={csvDownloading}
            >
              {csvDownloading ? "Downloading…" : "Download CSV"}
            </Button>
            <Button variant="primary" icon={<Plus size={16} aria-hidden />} onClick={openRaiseModal}>
              Raise Escalation
            </Button>
          </>
        }
      />

      <Card>
        <Toolbar>
          <Field label="Teacher" className="ds-toolbar__grow">
            {(ctl) => (
              <div className="esc-teacher-search-wrap">
                <TextInput
                  {...ctl}
                  type="search"
                  disabled={teachersFilterLoading}
                  placeholder={
                    teachersFilterLoading ? "Loading teachers…" : "Search teacher by name or ID…"
                  }
                  value={showTeacherFilterMenu ? teacherFilterQuery : selectedTeacherFilterLabel}
                  onChange={(e) => {
                    setTeacherFilterQuery(e.target.value);
                    setShowTeacherFilterMenu(true);
                  }}
                  onFocus={() => {
                    setTeacherFilterQuery("");
                    setShowTeacherFilterMenu(true);
                  }}
                  onBlur={() => {
                    window.setTimeout(() => setShowTeacherFilterMenu(false), 150);
                  }}
                  autoComplete="off"
                />
                {showTeacherFilterMenu && !teachersFilterLoading && (
                  <div className="esc-teacher-suggestions" role="listbox">
                    <button
                      type="button"
                      className="esc-teacher-suggestion-item"
                      role="option"
                      aria-selected={!teacherUserIdFilter}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickTeacherFilter("")}
                    >
                      <span className="esc-teacher-suggestion-name">All teachers</span>
                    </button>
                    {filteredTeacherFilterOptions.length === 0 ? (
                      <div className="esc-teacher-suggestion-empty">No teachers match</div>
                    ) : (
                      filteredTeacherFilterOptions.map((teacher) => (
                        <button
                          key={teacher.userId}
                          type="button"
                          className="esc-teacher-suggestion-item"
                          role="option"
                          aria-selected={teacherUserIdFilter === String(teacher.userId)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickTeacherFilter(String(teacher.userId))}
                        >
                          <span className="esc-teacher-suggestion-name">{teacher.label}</span>
                          <span className="esc-teacher-suggestion-meta">ID {teacher.userId}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field label="Date range" className="esc-filter-date">
            {(ctl) => (
              <DatePicker
                id={ctl.id}
                value={createdRangeValue}
                onChange={handleCreatedRangeChange}
                range
                format="DD/MM/YYYY"
                placeholder="Select date range"
                containerClassName="esc-date-range"
                inputClass="ds-input esc-date-range-input"
              />
            )}
          </Field>
          <Field label="Category">
            {(ctl) => (
              <Select
                {...ctl}
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setSubcategoryFilter("");
                  setPage(0);
                }}
                options={[
                  { value: "", label: "All categories" },
                  ...complaintNatureCounts.map(([label, count]) => ({ value: label, label: `${label} (${count})` })),
                ]}
              />
            )}
          </Field>
          <Field label="Validation status">
            {(ctl) => (
              <Select
                {...ctl}
                value={validationStatus}
                onChange={(e) => {
                  setValidationStatus(e.target.value as "" | TeacherEscalationValidationStatus);
                  setPage(0);
                }}
                options={VALIDATION_FILTERS.map((opt) => ({ value: opt.value, label: opt.label }))}
              />
            )}
          </Field>
          {activeTab !== "byTeacher" && (
            <Field label="Freshdesk status">
              {(ctl) => (
                <Select
                  {...ctl}
                  value={freshdeskStatusFilter}
                  onChange={(e) => {
                    setFreshdeskStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  options={FRESHDESK_STATUS_FILTERS.map((opt) => ({ value: opt.value, label: opt.label }))}
                />
              )}
            </Field>
          )}
          <Field label="Action taken">
            {(ctl) => (
              <Select
                {...ctl}
                value={actionTakenFilter}
                onChange={(e) => {
                  setActionTakenFilter(e.target.value);
                  setPage(0);
                }}
                options={[
                  { value: "", label: "All actions" },
                  ...ACTION_TAKEN_OPTIONS.map((a) => ({ value: a, label: a })),
                ]}
              />
            )}
          </Field>
        </Toolbar>
      </Card>

      {activeTab !== "byTeacher" && (
        <div className="esc-stats">
          <StatTile label="Open Escalations" tone="warning" value={loading ? "…" : stats.open} />
          <StatTile label="Resolved This Month" tone="success" value={loading ? "…" : stats.resolvedThisMonth} />
          <StatTile label="Validated Valid" tone="info" value={loading ? "…" : stats.validatedValid} />
          <StatTile label="Validated Invalid" tone="danger" value={loading ? "…" : stats.validatedInvalid} />
        </div>
      )}

      <Card>
        <div className="esc-panel">
          <Tabs items={tabItems} active={activeTab} onChange={selectTab} label="Escalation views" />

          {activeTab === "byTeacher" ? (
            <>
              <div className="esc-by-teacher-toolbar">
                <Field label="Flag at ≥ (valid)" className="esc-threshold">
                  {(ctl) => (
                    <TextInput
                      {...ctl}
                      type="number"
                      size="sm"
                      numeric
                      min={1}
                      value={flagThreshold}
                      onChange={(e) => {
                        const next = Math.max(1, Number(e.target.value) || 1);
                        setFlagThreshold(next);
                        setPage(0);
                      }}
                    />
                  )}
                </Field>
                <Field label="Sort by" className="esc-sort">
                  {(ctl) => (
                    <Select
                      {...ctl}
                      size="sm"
                      value={byTeacherSort}
                      onChange={(e) => {
                        setByTeacherSort(e.target.value);
                        setPage(0);
                      }}
                      options={BY_TEACHER_SORT_OPTIONS}
                    />
                  )}
                </Field>
                <Badge tone="danger">
                  <Flag size={12} aria-hidden /> Repeat (≥ {flagThreshold} valid)
                </Badge>
              </div>
              {loading ? (
                <Loader label="Loading teachers…" />
              ) : teacherRows.length === 0 ? (
                <EmptyState
                  title="No teachers found."
                  description="No teacher reaches the flag threshold for these filters. Lower the threshold or widen the date range."
                />
              ) : (
                <Table caption="Teachers with repeat escalations" className="esc-table esc-table--by-teacher">
                  <thead>
                    <tr>
                      <th>Teacher</th>
                      <th className="ds-num">All</th>
                      <th className="ds-num">Valid</th>
                      <th className="ds-num">Invalid</th>
                      <th>Last Escalation</th>
                      <th>Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teacherRows.map((row) => {
                      const isRepeat = row.flagged || row.valid >= flagThreshold;
                      const teacherLabel = String(row.teacherName || "").trim() || `User #${row.teacherUserId}`;
                      const genreName = String(row.genre?.name || "").trim();
                      return (
                        <tr key={row.teacherUserId} className={isRepeat ? "esc-row--flagged" : undefined}>
                          <td>
                            <div className="ds-strong">{teacherLabel}</div>
                            {genreName ? <div className="esc-meta ds-text-subtle">{genreName}</div> : null}
                          </td>
                          <td className="ds-num">{row.all}</td>
                          <td className="ds-num">
                            {row.valid > 0 ? <Badge tone="success">{row.valid}</Badge> : row.valid}
                          </td>
                          <td className="ds-num">
                            {row.invalid > 0 ? <Badge tone="danger">{row.invalid}</Badge> : row.invalid}
                          </td>
                          <td>{formatDateOnly(row.lastEscalationOn)}</td>
                          <td>
                            {isRepeat ? (
                              <Badge tone="danger">
                                <Flag size={12} aria-hidden /> Repeat
                              </Badge>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </>
          ) : loading ? (
            <Loader label="Loading escalations…" />
          ) : filteredRows.length === 0 ? (
            <EmptyState
              title="No escalations found."
              description="Try a wider date range, or set Category, Validation status and Action taken back to All."
            />
          ) : (
            <Table caption="Teacher escalations" className="esc-table">
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Genre</th>
                  <th aria-sort={sortBy === "createdBy" ? (sortOrder === "asc" ? "ascending" : "descending") : undefined}>
                    <button
                      type="button"
                      className="esc-sort-header"
                      onClick={toggleRaisedBySort}
                      title={`Sort Raised By ${sortBy === "createdBy" && sortOrder === "asc" ? "descending" : "ascending"}`}
                    >
                      Raised By
                      {sortBy !== "createdBy" ? (
                        <ChevronsDown size={14} aria-hidden="true" />
                      ) : sortOrder === "asc" ? (
                        <ChevronUp size={14} aria-hidden="true" />
                      ) : (
                        <ChevronDown size={14} aria-hidden="true" />
                      )}
                    </button>
                  </th>
                  <th>Category</th>
                  <th>Reason / Description</th>
                  <th>Raised On</th>
                  <th>Status</th>
                  <th>Action Taken</th>
                  <th>Action</th>
                  <th>Validated By</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const isPending = String(row.validationStatus || "PENDING").toUpperCase() === "PENDING";
                  const isValid = String(row.validationStatus || "").toUpperCase() === "VALID";
                  const isInvalid = String(row.validationStatus || "").toUpperCase() === "INVALID";
                  const actionNotes = getEscalationActionNotes(row);
                  const isSavingAction = actionSavingId === row.id;
                  const teacherLabel = row.teacherName || `User #${row.teacherUserId ?? "—"}`;
                  return (
                    <tr key={row.id}>
                      <td>
                        <button type="button" className="esc-link-btn" onClick={() => openDetail(row.id)}>
                          <span className="ds-strong">{teacherLabel}</span>
                          <span className="esc-meta ds-text-subtle">
                            {row.teacherMobileNumber || "—"}
                            {row.teacherUserId ? ` · ID ${row.teacherUserId}` : ""}
                            {row.freshdeskTicketId ? ` · FD ${row.freshdeskTicketId}` : ""}
                          </span>
                        </button>
                      </td>
                      <td>
                        <Badge>{getEscalationGenreName(row) || "—"}</Badge>
                      </td>
                      <td className="esc-nowrap">{row.createdBy || "—"}</td>
                      <td>
                        <Badge tone={categoryTone(row.category)}>
                          {row.category || "—"}
                          {row.subcategory ? ` / ${row.subcategory}` : ""}
                        </Badge>
                        {row.priority && <div className="esc-meta ds-text-subtle">Priority: {row.priority}</div>}
                      </td>
                      <td className="esc-reason">
                        <EscalationDescriptionCell description={row.description} link={row.link} />
                      </td>
                      <td className="esc-nowrap">{formatDisplayDate(row.createdOn)}</td>
                      <td>
                        <Badge tone={validationTone(row.validationStatus)}>
                          {isPending ? "Open" : validationLabel(row.validationStatus)}
                        </Badge>
                        {row.freshdeskStatus && <div className="esc-meta ds-text-subtle">FD: {row.freshdeskStatus}</div>}
                        {row.isDeleted && <div className="esc-meta esc-meta--danger">Deleted</div>}
                      </td>
                      <td className="esc-action-taken">
                        <Select
                          size="sm"
                          aria-label={`Action taken for ${teacherLabel}`}
                          disabled={isSavingAction}
                          value={actionNotes}
                          onChange={(e) => {
                            void handleRecordAction(row, e.target.value);
                          }}
                          title="Record action taken"
                          options={[
                            { value: "", label: "— Select action —" },
                            ...ACTION_TAKEN_OPTIONS.map((opt) => ({ value: opt, label: opt })),
                          ]}
                        />
                        {isSavingAction && <div className="esc-meta ds-text-subtle">Saving…</div>}
                      </td>
                      <td>
                        <div className="esc-validate-actions">
                          <Button
                            variant="secondary"
                            size="sm"
                            className={isValid ? "esc-validate-btn esc-validate-btn--valid" : "esc-validate-btn"}
                            aria-pressed={isValid}
                            onClick={() => openValidateModal(row, "VALID")}
                          >
                            ✓ Valid
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className={isInvalid ? "esc-validate-btn esc-validate-btn--invalid" : "esc-validate-btn"}
                            aria-pressed={isInvalid}
                            onClick={() => openValidateModal(row, "INVALID")}
                          >
                            ✗ Invalid
                          </Button>
                        </div>
                      </td>
                      <td>
                        {row.editedBy ? (
                          <div className="esc-validated-by">
                            <div className="ds-strong">{row.editedBy}</div>
                            <div className="esc-meta ds-text-subtle">{formatDisplayDate(row.editedOn)}</div>
                            {row.validationNotes && <div className="esc-meta ds-text-muted">{row.validationNotes}</div>}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          <Pager
            summary={`Page ${Math.min(page + 1, Math.max(totalPages, 1))} of ${Math.max(totalPages, 1)}`}
            canPrev={!(loading || page <= 0)}
            canNext={!(loading || page + 1 >= totalPages)}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </div>
      </Card>

      <Modal
        isOpen={raiseModalOpen}
        toggle={closeRaiseModal}
        centered
        size="md"
        modalClassName="teacher-escalation-modal"
      >
        <ModalHeader toggle={closeRaiseModal}>Raise an Escalation</ModalHeader>
        <ModalBody>
          <div className="esc-form">
            <Field
              label="Teacher name"
              required
              hint={teacherSearchLoading ? "Searching…" : "Type at least 2 letters of the name, or 4 digits of the mobile number."}
            >
              {(ctl) => (
                <div className="esc-teacher-search-wrap">
                  <TextInput
                    {...ctl}
                    type="text"
                    placeholder="Search teacher by name or mobile..."
                    value={raiseForm.teacherQuery}
                    onChange={(e) => handleTeacherQueryChange(e.target.value)}
                    onFocus={() => teacherSuggestions.length > 0 && setShowTeacherSuggestions(true)}
                    onBlur={() => {
                      window.setTimeout(() => setShowTeacherSuggestions(false), 150);
                    }}
                    autoComplete="off"
                  />
                  {showTeacherSuggestions && teacherSuggestions.length > 0 && (
                    <div className="esc-teacher-suggestions" role="listbox">
                      {teacherSuggestions.map((teacher, idx) => (
                        <button
                          key={`${teacher.teacherId}-${idx}`}
                          type="button"
                          className="esc-teacher-suggestion-item"
                          role="option"
                          aria-selected={false}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => void selectTeacherSuggestion(teacher)}
                        >
                          <span className="esc-teacher-suggestion-name">{teacher.name}</span>
                          {(teacher.phone || teacher.teacherUserId) && (
                            <span className="esc-teacher-suggestion-meta">
                              {[teacher.phone, teacher.teacherUserId ? `ID ${teacher.teacherUserId}` : ""]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Field>
            {raiseForm.teacherUserId && (
              <div className="esc-teacher-selected">
                Selected: {raiseForm.teacherName || "Teacher"}
                {raiseForm.teacherPhone ? ` · ${raiseForm.teacherPhone}` : ""}
                {` · ID ${raiseForm.teacherUserId}`}
              </div>
            )}

            <Field label="Teacher genre / course category" required>
              {(ctl) => (
                <Select
                  {...ctl}
                  value={raiseForm.teacherGenre}
                  onChange={(e) => setRaiseForm((prev) => ({ ...prev, teacherGenre: e.target.value }))}
                  options={raiseGenreSelectOptions}
                />
              )}
            </Field>

            <div className="esc-form-grid">
              <Field label="Category" required>
                {(ctl) => (
                  <Select
                    {...ctl}
                    value={raiseForm.category}
                    onChange={(e) =>
                      setRaiseForm((prev) => ({
                        ...prev,
                        category: e.target.value as EscalationCategory | "",
                        subcategory: "",
                      }))
                    }
                    options={[
                      { value: "", label: "Select category" },
                      ...ESCALATION_CATEGORIES.map((cat) => ({ value: cat, label: cat })),
                    ]}
                  />
                )}
              </Field>
              <Field label="Sub category" required>
                {(ctl) => (
                  <Select
                    {...ctl}
                    value={raiseForm.subcategory}
                    disabled={!raiseSubcategoryOptions.length}
                    onChange={(e) => setRaiseForm((prev) => ({ ...prev, subcategory: e.target.value }))}
                    options={[
                      { value: "", label: "Select sub category" },
                      ...raiseSubcategoryOptions.map((sub) => ({ value: sub, label: sub })),
                    ]}
                  />
                )}
              </Field>
              <Field label="Priority" required>
                {(ctl) => (
                  <Select
                    {...ctl}
                    value={raiseForm.priority}
                    onChange={(e) =>
                      setRaiseForm((prev) => ({ ...prev, priority: e.target.value as Priority | "" }))
                    }
                    options={[
                      { value: "", label: "Select priority" },
                      ...PRIORITY_OPTIONS.map((opt) => ({ value: opt, label: opt })),
                    ]}
                  />
                )}
              </Field>
              <Field label="Action taken (optional)">
                {(ctl) => (
                  <Select
                    {...ctl}
                    value={raiseForm.actionTaken}
                    onChange={(e) =>
                      setRaiseForm((prev) => ({ ...prev, actionTaken: e.target.value as ActionTaken | "" }))
                    }
                    options={[
                      { value: "", label: "Select action" },
                      ...ACTION_TAKEN_OPTIONS.map((opt) => ({ value: opt, label: opt })),
                    ]}
                  />
                )}
              </Field>
            </div>

            <Field label="Reason / description" required hint={`${raiseForm.description.length} / ${REASON_MAX}`}>
              {(ctl) => (
                <TextArea
                  {...ctl}
                  rows={4}
                  placeholder="Describe the issue clearly..."
                  value={raiseForm.description}
                  onChange={(e) => {
                    if (e.target.value.length > REASON_MAX) return;
                    setRaiseForm((prev) => ({ ...prev, description: e.target.value }));
                  }}
                />
              )}
            </Field>

            <Field label="Supporting evidence / link (optional)">
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="text"
                  placeholder="Paste link to recording, screenshot, or class log..."
                  value={raiseForm.link}
                  onChange={(e) => setRaiseForm((prev) => ({ ...prev, link: e.target.value }))}
                />
              )}
            </Field>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={closeRaiseModal} disabled={submittingRaise}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSubmitEscalation()} loading={submittingRaise}>
            {submittingRaise ? "Submitting…" : "Submit Escalation"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={validateModalOpen} toggle={closeValidateModal} centered size="md">
        <ModalHeader toggle={closeValidateModal}>
          {validateTarget?.validationStatus === "VALID" ? "Mark as Valid" : "Mark as Invalid"}
        </ModalHeader>
        <ModalBody>
          {validateTarget && (
            <div className="esc-form">
              <DescriptionList
                items={[
                  { label: "Escalation ID", value: validateTarget.id },
                  ...(validateTarget.teacherName ? [{ label: "Teacher", value: validateTarget.teacherName }] : []),
                  ...(validateTarget.category ? [{ label: "Category", value: validateTarget.category }] : []),
                ]}
              />
              <Field label="Reason" required hint="This reason is sent as validation notes to the API.">
                {(ctl) => (
                  <TextArea
                    {...ctl}
                    rows={4}
                    placeholder={
                      validateTarget.validationStatus === "VALID"
                        ? "Why is this escalation valid?"
                        : "Why is this escalation invalid?"
                    }
                    value={validateNotes}
                    onChange={(e) => setValidateNotes(e.target.value)}
                  />
                )}
              </Field>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={closeValidateModal} disabled={submittingValidate}>
            Cancel
          </Button>
          <Button
            variant={validateTarget?.validationStatus === "VALID" ? "primary" : "danger"}
            onClick={() => void handleSubmitValidation()}
            disabled={!validateNotes.trim()}
            loading={submittingValidate}
          >
            {submittingValidate
              ? "Saving…"
              : validateTarget?.validationStatus === "VALID"
                ? "Confirm Valid"
                : "Confirm Invalid"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={detailOpen} toggle={() => setDetailOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setDetailOpen(false)}>Escalation Details</ModalHeader>
        <ModalBody>
          {detailLoading ? (
            <Loader label="Loading escalation…" />
          ) : detailRow ? (
            <div className="esc-form">
              <DescriptionList
                items={[
                  { label: "ID", value: detailRow.id },
                  { label: "Teacher", value: detailRow.teacherName || "—" },
                  { label: "Teacher Mobile", value: detailRow.teacherMobileNumber || "—" },
                  { label: "Teacher User Id", value: detailRow.teacherUserId ?? "—" },
                  { label: "Student User Id", value: detailRow.studentUserId ?? "—" },
                  { label: "Category", value: detailRow.category || "—" },
                  { label: "Subcategory", value: detailRow.subcategory || "—" },
                  { label: "Teacher Genre", value: getEscalationGenreName(detailRow) || "—" },
                  { label: "Action Taken", value: getEscalationActionNotes(detailRow) || "—" },
                  { label: "Priority", value: detailRow.priority || "—" },
                  { label: "Validation", value: detailRow.validationStatus || "—" },
                  { label: "Freshdesk Ticket", value: detailRow.freshdeskTicketId ?? "—" },
                  { label: "Freshdesk Status", value: detailRow.freshdeskStatus || "—" },
                  { label: "Created By", value: detailRow.createdBy || "—" },
                  { label: "Created On", value: formatDisplayDate(detailRow.createdOn) },
                  { label: "Edited By", value: detailRow.editedBy || "—" },
                  { label: "Edited On", value: formatDisplayDate(detailRow.editedOn) },
                  {
                    label: "Link",
                    value: detailRow.link ? (
                      <a href={detailRow.link} target="_blank" rel="noreferrer">{detailRow.link}</a>
                    ) : (
                      "—"
                    ),
                  },
                ]}
              />
              <section className="esc-detail-section">
                <h3 className="esc-detail-heading">Description</h3>
                <p className="esc-reason-text">{detailRow.description || "—"}</p>
              </section>
              <section className="esc-detail-section">
                <h3 className="esc-detail-heading">Validation Notes</h3>
                <p className="esc-reason-text">{detailRow.validationNotes || "—"}</p>
              </section>
            </div>
          ) : (
            <EmptyState title="No details." description="This escalation could not be loaded. Close and try again." />
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
};

export default TeacherEscalationPage;
